import type { Task, FullProject } from '../../types';
import {
  computePersonKey,
  getVorgangColumn,
  isLegacyActiveContact,
} from './personModel';

export interface MigrationPreviewItem {
  taskId: string | number;
  personName: string;
  vorgangTitle: string;
  projectId: string;
  projectName: string;
  originalStatus: string;
  targetColumn: string;
}

export interface MigrationPlan {
  totalLegacyFound: number;
  items: MigrationPreviewItem[];
  apply: () => FullProject[];
}

/**
 * Prepares a non-destructive migration of legacy contact cards that sit in action columns.
 * Splits each into:
 *   1) A pure Person record (status: 'pool', dateless)
 *   2) A linked Vorgang (status in action column, carrying notes/checklist/deadline)
 */
export function planLegacyContactMigration(projects: FullProject[]): MigrationPlan {
  const items: MigrationPreviewItem[] = [];

  projects.forEach(project => {
    (project.tasks || []).forEach(t => {
      if (isLegacyActiveContact(t)) {
        const personName = (t.assignee || t.name || 'Unbenannt').trim();
        const vorgangTitle = (t.name && t.name !== t.assignee ? t.name : 'Abstimmung').trim();
        const col = getVorgangColumn(t) || 'open';
        items.push({
          taskId: t.id,
          personName,
          vorgangTitle,
          projectId: project.id,
          projectName: project.name || project.id,
          originalStatus: t.status || 'open',
          targetColumn: col
        });
      }
    });
  });

  const apply = (): FullProject[] => {
    return projects.map(project => {
      const hasLegacy = (project.tasks || []).some(isLegacyActiveContact);
      if (!hasLegacy) return project;

      const newTasks: Task[] = [];
      (project.tasks || []).forEach(t => {
        if (!isLegacyActiveContact(t)) {
          newTasks.push(t);
          return;
        }

        const personName = (t.assignee || t.name || 'Unbenannt').trim();
        const vorgangTitle = (t.name && t.name !== t.assignee ? t.name : 'Abstimmung').trim();
        const col = getVorgangColumn(t) || 'open';
        const personId = `contact_person_${t.id}`;
        const pKey = t.personKey || computePersonKey({ contactEmail: t.contactEmail, name: personName });

        // 1. The pure Person Master Record (lives in the pool)
        const personRecord: Task = {
          ...t,
          id: personId,
          type: 'contact',
          name: personName,
          assignee: personName,
          status: 'pool',
          start: undefined as unknown as string,
          end: undefined as unknown as string,
          personKey: pKey,
          isInlineNew: false
        };

        // 2. The active Vorgang (lives in the target column)
        const vorgangRecord: Task = {
          ...t,
          id: t.id,
          type: 'task',
          name: vorgangTitle,
          assignee: personName,
          contactId: personId,
          personKey: pKey,
          category: 'Kommunikation',
          status: col === 'completed' ? 'completed' : col,
          completed: col === 'completed',
          completedAt: col === 'completed' ? (t.completedAt || Date.now()) : undefined,
          isInlineNew: false
        };

        newTasks.push(personRecord, vorgangRecord);
      });

      return {
        ...project,
        tasks: newTasks,
        lastModified: Date.now()
      };
    });
  };

  return {
    totalLegacyFound: items.length,
    items,
    apply
  };
}
